import { format } from 'date-fns';
import { UserProfile, AuditLog } from '../types';
import AuditService from './AuditService';

class ExportService {
  /**
   * Convert data to CSV format
   */
  private convertToCSV(data: any[], headers: string[]): string {
    const headerRow = headers.join(',');
    const dataRows = data.map((row) => {
      return headers
        .map((header) => {
          const value = row[header];
          // Escape commas and quotes
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(',');
    });

    return [headerRow, ...dataRows].join('\n');
  }

  /**
   * Download CSV file
   */
  private downloadCSV(csv: string, filename: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Export users to CSV
   */
  async exportUsersCSV(users: UserProfile[]): Promise<void> {
    try {
      const headers = [
        'uid',
        'email',
        'displayName',
        'role',
        'createdAt',
        'lastLoginAt',
      ];

      const data = users.map((user) => ({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        role: user.role,
        createdAt: format(user.createdAt, 'yyyy-MM-dd HH:mm:ss'),
        lastLoginAt: user.lastLoginAt
          ? format(user.lastLoginAt, 'yyyy-MM-dd HH:mm:ss')
          : 'Never',
      }));

      const csv = this.convertToCSV(data, headers);
      const filename = `users_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;

      this.downloadCSV(csv, filename);

      // Log the export action
      await AuditService.logAdminAction('export_data', {
        exportType: 'users',
        recordCount: users.length,
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      throw new Error('Failed to export users');
    }
  }

  /**
   * Export audit logs to CSV
   */
  async exportAuditLogsCSV(auditLogs: AuditLog[]): Promise<void> {
    try {
      const headers = [
        'timestamp',
        'adminEmail',
        'action',
        'targetUserEmail',
        'details',
      ];

      const data = auditLogs.map((log) => ({
        timestamp: format(log.timestamp, 'yyyy-MM-dd HH:mm:ss'),
        adminEmail: log.adminEmail,
        action: log.action,
        targetUserEmail: log.targetUserEmail || '',
        details: JSON.stringify(log.details),
      }));

      const csv = this.convertToCSV(data, headers);
      const filename = `audit_logs_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;

      this.downloadCSV(csv, filename);

      // Log the export action
      await AuditService.logAdminAction('export_data', {
        exportType: 'audit_logs',
        recordCount: auditLogs.length,
      });
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw new Error('Failed to export audit logs');
    }
  }

  /**
   * Export analytics data to CSV
   */
  async exportAnalyticsCSV(analyticsData: any[]): Promise<void> {
    try {
      const headers = Object.keys(analyticsData[0] || {});
      const csv = this.convertToCSV(analyticsData, headers);
      const filename = `analytics_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;

      this.downloadCSV(csv, filename);

      // Log the export action
      await AuditService.logAdminAction('export_data', {
        exportType: 'analytics',
        recordCount: analyticsData.length,
      });
    } catch (error) {
      console.error('Error exporting analytics:', error);
      throw new Error('Failed to export analytics');
    }
  }
}

export default new ExportService();
